<template>
    <!-- A local provider and in-flow viewport so this toast sits above the composer rather than in
         the layout's global bottom-right stack. -->
    <ToastProvider>
        <div class="w-screen md:w-full md:max-w-(--breakpoint-md) md:mx-auto shrink-0 mb-2 px-2">
            <BaseToast v-model:open="isOpen" theme="info" :duration="Infinity" :action-text="t('components.returnToChat.startNew')">
                <!-- Own paragraph: the default one caps at 320px, which is for corner toasts, not one
                     that spans the composer. -->
                <template #description>
                    <p>{{ t("components.returnToChat.body") }}</p>
                </template>
                <template #action>
                    <BaseLink
                        :href="startChatUrl"
                        :data="{ chat_source: CHATSOURCE.RETURN_TO_CHAT }"
                        class="base-link scaffold-body-small-medium whitespace-nowrap"
                        @click="handleStartNewSession"
                        @finish="handleNewSessionVisitFinished">
                        {{ t("components.returnToChat.startNew") }}
                    </BaseLink>
                </template>
            </BaseToast>
            <ToastViewport class="list-none m-0 p-0 [&_.toastRoot]:ml-0" />
        </div>
    </ToastProvider>
</template>

<script setup lang="ts">
import type { GlobalProperties } from "~vue/appTypes";
import BaseToast from "~vue/components/BaseToast.vue";
import BaseLink from "~vue/router/BaseLink.vue";
import { CHATSOURCE } from "~vue/utils/constants";
import { logUserInteraction } from "~vue/utils/logUtils";
import { ToastProvider, ToastViewport } from "reka-ui";
import { inject, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const emit = defineEmits<{ "continue-chat": [] }>();

const { t } = useI18n();
const coachingSessionUuid = inject("coachingSessionUuid");
const { $startChatUrl: startChatUrl } = inject<GlobalProperties>("globalProperties", {} as GlobalProperties);

const isOpen = ref(true);
let startedNewSession = false;
let isUnmounted = false;

onMounted(() => {
    logUserInteraction("return_to_chat_dialog_shown", { coaching_session_id: coachingSessionUuid });
});

onBeforeUnmount(() => {
    isUnmounted = true;
});

const handleStartNewSession = () => {
    startedNewSession = true;
    logUserInteraction("return_to_chat_start_new_session_clicked", { coaching_session_id: coachingSessionUuid });
};

// Clicking the action closes the toast. A successful visit replaces this page; if we are still
// mounted once it has rendered, the visit failed or was cancelled, so bring the toast back to retry.
const handleNewSessionVisitFinished = async () => {
    await nextTick();
    if (isUnmounted) {
        return;
    }
    startedNewSession = false;
    isOpen.value = true;
};

// Dismissing (the X, Escape or a swipe) is the old "Continue Chat" choice. The action link also
// closes the toast, so skip the continue event when that is what closed it.
watch(isOpen, (open) => {
    if (open || startedNewSession) {
        return;
    }
    logUserInteraction("return_to_chat_continue_chat_clicked", { coaching_session_id: coachingSessionUuid });
    emit("continue-chat");
});
</script>
